import type { Category, PlanningCycle, Todo } from "../domain/entities.ts";
import type { AppView, SavedView } from "../domain/app-types.ts";
import type { SyncState } from "../infrastructure/sync/automerge.ts";
import { incompleteTaskDependencies } from "../features/tasks/productivity.ts";
import { taskIsArchived } from "../features/tasks/task-visibility.ts";
import { cycleSummaryView, shellViewTitle, syncStateLabel } from "./shell-content-view.ts";
import { emptyTaskStateView, topbarActionsView } from "./shell-view.ts";
import { activeFilters } from "../features/navigation/active-filters.ts";
import type { ViewFilters } from "../domain/app-types.ts";

type IconRenderer = (name: string) => string;

export interface ShellPresentationCapabilities {
  view(): AppView;
  query(): string;
  filters?(): ViewFilters;
  activeSavedView(): string | null;
  savedViews(): SavedView[];
  activeCycle(): string | null;
  cycles(): PlanningCycle[];
  todos(): Todo[];
  categories(): Category[];
  activeCollection(): string | null;
  categoryName(id: string): string;
  activeRoot(): string | null;
  activeRootName?(): string;
  canEdit(): boolean;
  syncState(): SyncState;
  unreadNotifications(): number;
  pendingInvitations(): number;
  mobileActions(): readonly string[];
  formatDate(value: string): string;
  icon: IconRenderer;
}

export function createShellPresentationRuntime(capabilities: ShellPresentationCapabilities) {
  const archived = (todo: Todo): boolean => taskIsArchived(todo, capabilities.categories());

  function cycleSummary(showAction = true): string {
    const cycle = capabilities.cycles().find(item => item.id === capabilities.activeCycle());
    const todos = capabilities.todos();
    const tasks = todos.filter(todo => !todo.deleted && todo.cycleId === cycle?.id);
    const ready = tasks.filter(todo => !todo.completed && !incompleteTaskDependencies(todo, todos).length).length;
    return cycleSummaryView({ cycle, tasks, readyCount: ready, canEdit: capabilities.canEdit(), showAction, formatDate: capabilities.formatDate, icon: capabilities.icon });
  }

  const emptyState = (): string => emptyTaskStateView({
    view: capabilities.view(), query: capabilities.query(), hasActiveRoot: !!capabilities.activeRoot(), canEdit: capabilities.canEdit(), icon: capabilities.icon,
    scopeName: capabilities.activeCollection() ? capabilities.categoryName(capabilities.activeCollection()!) : capabilities.activeRootName?.(),
    hasActiveCycle: !!capabilities.activeCycle(),
    filtered: capabilities.view() === "custom" && (capabilities.filters ? activeFilters(capabilities.filters(), { assignee: () => "", collection: () => "", cycle: () => "" }).length > 0 : true),
    completedCount: capabilities.todos().filter(todo => !todo.deleted && !archived(todo) && todo.completed && (!capabilities.activeCollection() || todo.categoryId === capabilities.activeCollection())).length,
  });

  const viewTitle = (): string => shellViewTitle({
    view: capabilities.view(), activeSavedView: capabilities.activeSavedView(), savedViews: capabilities.savedViews(),
    activeCycle: capabilities.activeCycle(), cycles: capabilities.cycles(),
    collectionName: capabilities.activeCollection() ? capabilities.categoryName(capabilities.activeCollection()!) : undefined,
  });

  const syncLabel = (): string => syncStateLabel(capabilities.syncState());

  const topbarActions = (canCreateTasks: boolean): string => topbarActionsView({
    canCreateTasks, syncState: capabilities.syncState(), syncLabel: syncLabel(), unreadNotifications: capabilities.unreadNotifications(),
    pendingInvitations: capabilities.pendingInvitations(), mobileActions: capabilities.mobileActions(), view: capabilities.view(), icon: capabilities.icon,
    searchShortcut: typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform) ? "⌘ K" : "Ctrl K",
  });

  return { archived, cycleSummary, emptyState, viewTitle, syncLabel, topbarActions };
}
